import { useMemo, useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getBuildingsRequest } from 'actions/buildingActions';
import { LinkBuildingDialog } from '../linkBuildingDialog/LinkBuildingDialog';
import { PageHeader } from 'ui/shared/pageHeader';
import { getBuildings, isLoadingBuildings, getLastSearchOrder } from 'selectors/buildingsSelectors';
import { BuildingFilter } from '../buildingsFilter/BuildingsFilter';
import { useSession, useLocalStorage } from 'hooks';
import { Permission } from 'shared/permissions';
import { BuildingList } from '../buildingList/BuildingList';

require('./BuildingListPage.css');

export const BuildingListPage = () => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();

    const buildings = useSelector(getBuildings);
    const order = useSelector(getLastSearchOrder);
    //const more = useSelector(getLastSearchMore);
    const loading = useSelector(isLoadingBuildings);
    const fourHourExpiryTime = 4 * 60 * 60 * 1000;

    const [ linkDialogVisible, setLinkDialogVisible ] = useState(false);

    const [ searchText, setSearchText ] = useLocalStorage('buildings-filter-text', '');
    const [ searchTextExpiry, setSearchTextExpiry ] = useLocalStorage('buildings-filter-text-expiry', Date.now() + fourHourExpiryTime);
    const [ sortCriteria ] = useState('status');
    const [ sortDirection ] = useState('asc');

    const filter = useMemo(() => ({
        searchText,
        sortCriteria,
        sortDirection,
        maxRows: 100, // TODO: parameterize this
        page: 1, // TODO: parameterize this
        more: false, // TODO: change this if building requests more
        // tells saga whether to continue getting building chunks
        continueBuildingSearch: () => false,
    }), [ searchText, sortCriteria, sortDirection ]);
    
    // check to see if filter needs to be reset
    // everytime component is mounted
    useEffect(() => {
        if (Date.now() > searchTextExpiry) {
            setSearchText('');
        }
    }, [ setSearchText, searchTextExpiry ]);

    const handleChange = useCallback((text: string) => {
        setSearchText(text);
        setSearchTextExpiry(Date.now() + fourHourExpiryTime);
    }, [ setSearchText, setSearchTextExpiry ]);

    const dispatch = useDispatch();

    const handleBuildingLinked = useCallback(() => {
        // TODO: would be nice to clean this up... we're receiving the building
        //       but we don't have enough info to properly put the building into
        //       the store. But it's end of day on Friday and I promised Calvin
        //       that I'd get the updates on the dev server today. So for now
        //       just kick off a new request for all buildings.
        dispatch(getBuildingsRequest(filter));

        setLinkDialogVisible(false);
    }, [ filter, dispatch, setLinkDialogVisible ]);

    
    // tell saga we can start getting building chunks
    useEffect(() => {
        dispatch(getBuildingsRequest(filter));
    }, [ dispatch, filter ]);

    return (
        <div id="building-list-page">
            <PageHeader
                title={ t('BUILDINGS.TITLE') }
                subTitle={ '' }
            />
            <div className='content'>
                <BuildingList
                    buildings={ buildings }
                    order={ order }
                    loading={ loading }
                />
                <div>
                    { hasPermission(null, Permission.LinkOrganization) &&
                        <div className='building-list-commands'>
                            <button
                                type="button"
                                className="ui basic button"
                                onClick={ () => setLinkDialogVisible(true) }
                            >
                                { t('COMMAND.ADD_BUILDING') }
                            </button>
                        </div>
                    }
                    { hasPermission(null, Permission.AllBuildings) &&
                        <BuildingFilter
                            textFilter={ searchText }
                            onChange={ handleChange }
                        />
                    }
                    <LinkBuildingDialog
                        visible={ linkDialogVisible }
                        onLinked={ handleBuildingLinked }
                        onCancel={ () => setLinkDialogVisible(false) }
                    />
                </div>
            </div>
        </div>
    );
};
